import { useEffect, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { type Shortlink, shortLinksApi } from "@/lib/short-links/short-links-api";

interface Props {
	link: Shortlink;
	onClose: () => void;
}

/**
 * QR code d'un lien court.
 *
 * L'image est produite par l'API, pas par le navigateur : un QR imprimé sur une
 * affiche ou une facture doit être identique quel que soit le poste qui l'a
 * généré.
 *
 * Le contenu encodé porte `?q=1`, ce qui permet de distinguer un scan d'un clic
 * ordinaire dans les statistiques — le paramètre est consommé et retiré par le
 * redirecteur, il ne pollue donc pas l'adresse finale.
 */
export function QrModal({ link, onClose }: Props) {
	const [src, setSrc] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let revoke: string | null = null;
		void shortLinksApi
			.qrBlob(link.id, 512)
			.then((url) => {
				revoke = url;
				setSrc(url);
			})
			.catch(() =>
				setError(
					"QR indisponible. Les liens système en sont exclus : leur destination contient un jeton d'accès à la commande.",
				),
			);
		return () => {
			if (revoke) URL.revokeObjectURL(revoke);
		};
	}, [link.id]);

	const download = async (size: number) => {
		const url = await shortLinksApi.qrBlob(link.id, size);
		const a = document.createElement("a");
		a.href = url;
		a.download = `qr-${link.code}-${size}.png`;
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<Modal title={`QR — ${link.code}`} subtitle={link.smsForm} onClose={onClose} width={420}>
			<div style={{ textAlign: "center", padding: "8px 0" }}>
				{error ? (
					<div className="muted" style={{ padding: 24 }}>
						{error}
					</div>
				) : src ? (
					<img
						src={src}
						alt={`QR code du lien ${link.code}`}
						style={{
							width: 256,
							height: 256,
							border: "1px solid var(--a-border)",
							borderRadius: "var(--a-r)",
							background: "#fff",
						}}
					/>
				) : (
					<div className="muted" style={{ padding: 48 }}>
						Génération…
					</div>
				)}
			</div>

			<ModalFooter>
				<AdminButton onClick={onClose}>Fermer</AdminButton>
				{!error ? (
					<>
						<AdminButton onClick={() => void download(512)}>PNG 512</AdminButton>
						<AdminButton variant="primary" onClick={() => void download(1024)}>
							PNG 1024
						</AdminButton>
					</>
				) : null}
			</ModalFooter>
		</Modal>
	);
}
